import { ArrowLeft, Moon, Sun, Palette, Check, Star, Clock, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { goBackOrHome } from '../../lib/navigation';
import { useThemeStore } from '../../lib/themeStore';

export function ThemeTest() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useThemeStore();

  return (
    <div className="card shadow-lg border-0">
      <div className="card-body p-5">
        <button
          className="btn btn-link text-primary mb-4 p-0 d-flex align-items-center"
          onClick={() => goBackOrHome(navigate)}
        >
          <ArrowLeft size={20} className="me-2" />
          Retour
        </button>

        <div className="text-center mb-5">
          <Palette className="h-12 w-12 text-primary mb-2" />
          <h2 className="card-title text-center mb-2">Test du Dark Theme</h2>
          <p className="text-muted">
            Tous les composants s'adaptent automatiquement au thème choisi
          </p>
        </div>

        {/* Theme Toggle Section */}
        <div className="card mb-5 border-2" style={{ borderColor: 'var(--bs-primary)' }}>
          <div className="card-body text-center">
            <div className="row align-items-center">
              <div className="col-md-4">
                <div className="d-flex align-items-center justify-content-center">
                  <Sun className="me-2" size={24} />
                  <span className="fw-bold">Mode Clair</span>
                </div>
              </div>
              <div className="col-md-4">
                <button
                  className="btn btn-primary btn-lg d-flex align-items-center mx-auto"
                  onClick={toggleTheme}
                >
                  {theme === 'dark' ? <Sun className="me-2" /> : <Moon className="me-2" />}
                  Basculer vers {theme === 'dark' ? 'Clair' : 'Sombre'}
                </button>
              </div>
              <div className="col-md-4">
                <div className="d-flex align-items-center justify-content-center">
                  <Moon className="me-2" size={24} />
                  <span className="fw-bold">Mode Sombre</span>
                </div>
              </div>
            </div>
            <div className="mt-3">
              <small className="text-muted">
                Thème actuel: <strong className="text-primary">{theme === 'dark' ? 'Sombre' : 'Clair'}</strong>
              </small>
            </div>
          </div>
        </div>

        {/* Composants de test */}
        <div className="row">
          {/* Cards & Buttons */}
          <div className="col-md-6 mb-4">
            <h5 className="mb-3 d-flex align-items-center">
              <span className="badge bg-primary me-2">1</span>
              Cards & Boutons
            </h5>
            <div className="card mb-3">
              <div className="card-header">
                <h6 className="mb-0">En-tête de carte</h6>
              </div>
              <div className="card-body">
                <h5 className="card-title">Titre de carte</h5>
                <p className="card-text text-muted">
                  Texte d'exemple pour tester l'adaptation du thème.
                </p>
                <button className="btn btn-primary me-2">Primaire</button>
                <button className="btn btn-secondary me-2">Secondaire</button>
                <button className="btn btn-outline-primary">Outlined</button>
              </div>
            </div>
          </div>

          {/* Forms */}
          <div className="col-md-6 mb-4">
            <h5 className="mb-3 d-flex align-items-center">
              <span className="badge bg-primary me-2">2</span>
              Formulaires
            </h5>
            <div className="card mb-3">
              <div className="card-body">
                <form>
                  <div className="mb-3">
                    <label htmlFor="email" className="form-label">Email</label>
                    <input type="email" className="form-control" id="email" placeholder="exemple@hockey.com" />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="select" className="form-label">Sélection</label>
                    <select className="form-select" id="select">
                      <option>Choisir une option...</option>
                      <option>Option 1</option>
                      <option>Option 2</option>
                    </select>
                  </div>
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" id="switch" />
                    <label className="form-check-label" htmlFor="switch">
                      Activer les notifications
                    </label>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Navigation & Alerts */}
          <div className="col-md-6 mb-4">
            <h5 className="mb-3 d-flex align-items-center">
              <span className="badge bg-primary me-2">3</span>
              Navigation & Alertes
            </h5>
            <div>
              <ul className="nav nav-pills mb-3">
                <li className="nav-item">
                  <button className="nav-link active">Actif</button>
                </li>
                <li className="nav-item">
                  <button className="nav-link">Normal</button>
                </li>
                <li className="nav-item">
                  <button className="nav-link">Autre</button>
                </li>
              </ul>
              
              <div className="alert alert-info mb-3">
                <strong>Info:</strong> Exemple d'alerte d'information
              </div>
              
              <div className="list-group">
                <div className="list-group-item d-flex align-items-center">
                  <User className="me-2" size={16} />
                  <span><strong>Joueur 1:</strong> Connor McDavid - 50 points</span>
                </div>
                <div className="list-group-item d-flex align-items-center">
                  <Star className="me-2" size={16} />
                  <span><strong>Joueur 2:</strong> Sidney Crosby - 45 points</span>
                </div>
                <div className="list-group-item d-flex align-items-center">
                  <Clock className="me-2" size={16} />
                  <span><strong>Joueur 3:</strong> Nathan MacKinnon - 48 points</span>
                </div>
              </div>
            </div>
          </div>

          {/* Tables & Progress */}
          <div className="col-md-6 mb-4">
            <h5 className="mb-3 d-flex align-items-center">
              <span className="badge bg-primary me-2">4</span>
              Tableaux & Progression
            </h5>
            <div>
              <table className="table table-striped mb-3">
                <thead>
                  <tr>
                    <th>Équipe</th>
                    <th>Points</th>
                    <th>Victoires</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Team Alpha</td>
                    <td>150</td>
                    <td>12</td>
                  </tr>
                  <tr>
                    <td>Team Beta</td>
                    <td>145</td>
                    <td>11</td>
                  </tr>
                  <tr>
                    <td>Team Gamma</td>
                    <td>140</td>
                    <td>10</td>
                  </tr>
                </tbody>
              </table>
              
              <div className="mb-3">
                <label className="form-label">Performance d'équipe</label>
                <div className="progress">
                  <div className="progress-bar bg-success" style={{ width: '75%' }}>75%</div>
                </div>
              </div>
              
              <div className="d-flex justify-content-center">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Chargement...</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Fonctionnalités du Dark Theme */}
        <div className="card mt-5">
          <div className="card-header">
            <h5 className="mb-0">✨ Fonctionnalités du Dark Theme</h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <ul className="list-unstyled">
                  <li className="mb-2 d-flex align-items-center">
                    <Check className="text-success me-2" size={16} />
                    Persistance automatique du thème
                  </li>
                  <li className="mb-2 d-flex align-items-center">
                    <Check className="text-success me-2" size={16} />
                    Transitions fluides
                  </li>
                  <li className="mb-2 d-flex align-items-center">
                    <Check className="text-success me-2" size={16} />
                    Tous les composants Bootstrap
                  </li>
                  <li className="mb-2 d-flex align-items-center">
                    <Check className="text-success me-2" size={16} />
                    Éléments de formulaire stylés
                  </li>
                </ul>
              </div>
              <div className="col-md-6">
                <ul className="list-unstyled">
                  <li className="mb-2 d-flex align-items-center">
                    <Check className="text-success me-2" size={16} />
                    Composants de navigation
                  </li>
                  <li className="mb-2 d-flex align-items-center">
                    <Check className="text-success me-2" size={16} />
                    Tableaux et barres de progression
                  </li>
                  <li className="mb-2 d-flex align-items-center">
                    <Check className="text-success me-2" size={16} />
                    Cards et alertes
                  </li>
                  <li className="mb-2 d-flex align-items-center">
                    <Check className="text-success me-2" size={16} />
                    Landing page exclue
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mt-4">
          <small className="text-muted">
            La landing page reste en thème clair pour un meilleur attrait marketing.
          </small>
        </div>
      </div>
    </div>
  );
}
